import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { ContentWidth } from '../ContentWidth/ContentWidth';
import { SliderField } from '../SliderField/SliderField';
import { useNode } from '@craftjs/core';
import type { PageProps } from '@builder4/shared/components/Page';

interface ContentWidthFullProps {
  className: string;
  onClick?: () => void;
  text: string;
}

export const ContentWidthFull: React.FC<ContentWidthFullProps> = ({ className = '', onClick }) => {
  const handleClick = () => {
    if (onClick) onClick();
  };

  const {
    actions: { setProp },
    maxWidth: nodeMaxWidth,
  } = useNode((node) => ({
    maxWidth: node.data.props?.maxWidth as string | undefined,
  }));

  const [contentWidth, setContentWidth] = useState<'s' | 'm' | 'l' | 'f' | ''>('s');
  const [maxWidthValue, setMaxWidthValue] = useState<number>(768);

  const SLIDER_MAX = 2560;

  // === Function to convert contentWidth to a number for SliderField
  const getMaxWidthValueFromContentWidth = (width: 's' | 'm' | 'l' | 'f' | '') => {
    switch (width) {
      case 's':
        return 768;
      case 'm':
        return 960;
      case 'l':
        return 1200;
      case 'f':
        return SLIDER_MAX; // full → use slider max
      default:
        return 0;
    }
  };

  // === Function to convert number to contentWidth
  const getContentWidthFromMaxWidthValue = (value: number): 's' | 'm' | 'l' | 'f' | '' => {
    switch (value) {
      case 768:
        return 's';
      case 960:
        return 'm';
      case 1200:
        return 'l';
      case SLIDER_MAX:
        return 'f';
      default:
        return '';
    }
  };

  const parsePxToNumber = (value?: string | number | null): number => {
    if (value == null) return 0;
    if (typeof value === 'number') return value;
    const trimmed = value.trim();
    if (/^max$/i.test(trimmed)) return SLIDER_MAX;
    if (trimmed === '100%') return SLIDER_MAX;
    if (trimmed.endsWith('px')) {
      const num = Number(trimmed.replace('px', ''));
      return isNaN(num) ? 0 : num;
    }
    const direct = Number(trimmed);
    return isNaN(direct) ? 0 : direct;
  };

  // === Sync from node prop → local state on mount/prop change
  useEffect(() => {
    const numeric = parsePxToNumber(nodeMaxWidth);
    setMaxWidthValue(numeric);
    setContentWidth(getContentWidthFromMaxWidthValue(numeric));
  }, [nodeMaxWidth]);

  const handleContentWidthChange = (newWidth: 's' | 'm' | 'l' | 'f' | '') => {
    setContentWidth(newWidth);
    const newValue = getMaxWidthValueFromContentWidth(newWidth);
    setMaxWidthValue(newValue);
    if (newWidth === 'f') {
      setProp((props: Partial<PageProps>) => {
        props.maxWidth = '100%';
      }, 300);
    } else if (newValue > 0) {
      setProp((props: Partial<PageProps>) => {
        props.maxWidth = `${newValue}px`;
      }, 300);
    }
  };

  const handleMaxWidthChange = (value: number) => {
    const clamped = Math.min(value, SLIDER_MAX);
    setMaxWidthValue(clamped);
    if (clamped === SLIDER_MAX) {
      if (contentWidth !== 'f') setContentWidth('f');
      setProp((props: Partial<PageProps>) => {
        props.maxWidth = '100%';
      }, 300);
      return;
    }
    const mapped = getContentWidthFromMaxWidthValue(clamped);
    if (mapped !== contentWidth) setContentWidth(mapped);
    setProp((props: Partial<PageProps>) => {
      props.maxWidth = `${clamped}px`;
    }, 300);
  };

  return (
    <div onClick={handleClick} className={clsx('flex flex-col gap-3', className)}>
      <ContentWidth
        className=""
        contentWidth={contentWidth}
        onClick={handleContentWidthChange}
        text="Section Width"
      />
      <SliderField
        className="w-full max-w-[216px]"
        label="Custom"
        value={maxWidthValue}
        min={0}
        max={SLIDER_MAX}
        suffix="px"
        showMaxLabel={true}
        onChange={handleMaxWidthChange}
      />
    </div>
  );
};
